<template>
  <div class="dealers-ranking-list">
    <OurDropdownButton
      v-if="isMobileDevice"
      v-model="sortTypeModel"
      class="sorting-button mb-5 w-full"
      :text="sortingText"
      :options="sortOptions"
      item-label="name"
      variant="thirdary"
      filled
    />

    <template v-if="isMobileDevice">
      <div v-if="items.length">
        <DealerRankingCard
          v-for="item in items"
          :key="item.customerId"
          :item="item"
          @click="onClickDealer"
        />
      </div>
      <SkeletonLoaderCard v-else :table-headers="skeletonHeaders" />
    </template>

    <OurTable
      v-else
      :headers="tableHeaders"
      :items="items"
      :table-fixed="false"
      class="dealers-ranking-list-table"
      @sort="onSort"
      @clickRow="onClickRow"
    >
      <template #cell-place="{ value }">
        <span class="font-medium text-gray-900">{{ value }}</span>
      </template>

      <template #cell-dealer="{ value }">
        <div class="flex min-w-0 items-center gap-3">
          <OurAvatar :user-name="value.name" size="lg" rounded="full" />
          <div class="min-w-0">
            <div class="truncate font-medium text-gray-900">{{ value.name }}</div>
            <div class="text-sm text-gray-500">{{ $t("page.users.id") }}: {{ value.id }}</div>
          </div>
        </div>
      </template>

      <template #cell-dealerLevel="{ value }">
        <BonusProgramLevelBadge :level="value" />
      </template>

      <template #cell-incentiveLevel="{ value }">
        <IncentiveLevelProgressCell
          :level="value.level"
          :progress-percent="value.progressPercent"
        />
      </template>

      <template #cell-shipmentsSum="{ value }">
        <OurMoney weight="regular" size="sm" :sum="value" currency-symbol="$" />
      </template>
    </OurTable>

    <div
      v-if="paginationData?.total"
      class="mt-4 flex flex-wrap items-center justify-between gap-3"
    >
      <div class="flex flex-wrap items-center gap-3">
        <OurSelect
          :value="perPage"
          :label="$t('label.show')"
          class="w-40"
          :options="perPageOptions"
          item-label="label"
          value-key="value"
          :allow-empty="false"
          size="sm"
          @input="onPerPageChange"
        />
        <span class="text-sm text-gray-600">
          {{ $t("promotions.dealersList.ofTotal", { total: paginationData.total }) }}
        </span>
      </div>

      <OurPagination
        v-if="isMoreThanOnePage"
        :current-page="paginationData.currentPage"
        :per-page="paginationData.perPage"
        :total="paginationData.total"
        @pageChange="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import BonusProgramLevelBadge from "./BonusProgramLevelBadge.vue";
import DealerRankingCard from "./DealerRankingCard.vue";
import IncentiveLevelProgressCell from "./IncentiveLevelProgressCell.vue";

const SORTABLE_COLUMNS = [
  "place",
  "customer_name",
  "dealer_level",
  "incentive_level",
  "shipments_sum",
];

const SORT_OPTIONS = [
  { id: "place_desc", sortable: "-place", labelKey: "promotions.dealersList.sort.placeDesc" },
  { id: "place_asc", sortable: "place", labelKey: "promotions.dealersList.sort.placeAsc" },
  {
    id: "customer_name_asc",
    sortable: "customer_name",
    labelKey: "promotions.dealersList.sort.nameAsc",
  },
  {
    id: "customer_name_desc",
    sortable: "-customer_name",
    labelKey: "promotions.dealersList.sort.nameDesc",
  },
  {
    id: "shipments_sum_desc",
    sortable: "-shipments_sum",
    labelKey: "promotions.dealersList.sort.shipmentsDesc",
  },
  {
    id: "shipments_sum_asc",
    sortable: "shipments_sum",
    labelKey: "promotions.dealersList.sort.shipmentsAsc",
  },
];

export default {
  name: "DealersRankingList",

  components: {
    SkeletonLoaderCard,
    BonusProgramLevelBadge,
    DealerRankingCard,
    IncentiveLevelProgressCell,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: () => ({}),
    },

    orderBy: {
      type: Object,
      default: () => ({ column: "place", direction: "asc" }),
    },

    perPage: {
      type: Number,
      default: 10,
    },

    sortType: {
      type: String,
      default: "place_asc",
    },
  },

  data() {
    return {
      perPageOptions: [
        { value: 10, label: "10" },
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
      ],
    };
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),

    icons: () => ({
      Arrow: require("@/components/_bit/IconSvg/icons/Arrow-right.svg"),
    }),

    skeletonHeaders() {
      return [{ value: "place" }, { value: "dealer" }, { value: "shipmentsSum" }];
    },

    sortOptions() {
      return SORT_OPTIONS.map((option) => ({
        id: option.id,
        name: this.$t(option.labelKey),
        sortable: option.sortable,
      }));
    },

    sortingText() {
      return (
        this.sortOptions.find((option) => option.id === this.sortType)?.name ||
        this.$t("button.sorting")
      );
    },

    sortTypeModel: {
      get() {
        return this.sortType;
      },
      set(value) {
        this.$emit("sortTypeChange", value);
      },
    },

    tableHeaders() {
      return [
        {
          value: "place",
          text: this.$t("promotions.dealersList.place"),
          thClass: "min-w-[4rem]",
          tdClass: "min-w-[4rem]",
          sortable: "place",
          sortDirection: this.getSortDirection("place"),
        },
        {
          value: "dealer",
          text: this.$t("promotions.dealersList.dealer"),
          thClass: "min-w-[10rem]",
          tdClass: "min-w-[10rem]",
          sortable: "customer_name",
          sortDirection: this.getSortDirection("customer_name"),
        },
        {
          value: "dealerLevel",
          text: this.$t("promotions.dealersList.dealerLevel"),
          thClass: "min-w-[10rem]",
          tdClass: "min-w-[10rem]",
          sortable: "dealer_level",
          sortDirection: this.getSortDirection("dealer_level"),
        },
        {
          value: "incentiveLevel",
          text: this.$t("promotions.dealersList.incentiveLevel"),
          thClass: "min-w-[10rem]",
          tdClass: "min-w-[10rem]",
          sortable: "incentive_level",
          sortDirection: this.getSortDirection("incentive_level"),
        },
        {
          value: "shipmentsSum",
          text: this.$t("promotions.dealersList.shipmentsSum"),
          thClass: "min-w-[10rem]",
          tdClass: "min-w-[10rem]",
          sortable: "shipments_sum",
          sortDirection: this.getSortDirection("shipments_sum"),
        },
      ];
    },

    isMoreThanOnePage() {
      return (this.paginationData?.lastPage || 0) > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },
  },

  watch: {
    routePage(newPage) {
      if (newPage !== this.paginationData.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  methods: {
    getSortDirection(field) {
      if (this.orderBy.column !== field) {
        return "";
      }

      return this.orderBy.direction;
    },

    onSort(header) {
      if (!SORTABLE_COLUMNS.includes(header.sortable)) {
        return;
      }

      this.$emit("sort", header);
    },

    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    onPerPageChange(value) {
      this.$emit("perPageChange", value);
    },

    onClickRow(row) {
      this.onClickDealer(row.customerId);
    },

    onClickDealer(customerId) {
      this.$emit("clickDealer", customerId);
    },
  },
};
</script>
